import { type Dispatch, type SetStateAction, useEffect, useReducer, useState } from 'react'
import useApi from 'hooks/useApi'
import { WppActionButton, WppIconDone, WppTypography } from '@wppopen/components-library-react'
import DownloadAllButton from 'components/DownloadAllButton/DownloadAllButton'
import Table from 'components/table'
import TableMetaDataRow from 'components/table/TableMetaDataRow'
import { columnStructure } from './utils'
import {
  getPaginatedTableReducerEmptyState,
  paginatedTableReducer,
  setRowData,
  setTaskPosition,
  setTotalItemCount,
} from 'reducers/PaginatedTableReducer/PaginatedTableReducer'
import { GenericModal } from 'components/Modals'
import { DEFAULT_API_ITEM_LIMITS } from 'utils/constants'
import { handleLoadMore, handleSortingUpdate, refreshHandler } from '../../pages/ProjectView/utils'
import { downloadCSVFile } from 'utils'
import { useNavigate } from 'react-router-dom'
import useCurrentUser from 'hooks/useCurrentUser'
import type { ToastDetails } from 'components/Banner/BannerTypesUtils.d'
import type { ApproveCreateData, JobsListData } from '@contracts'
import type { RowClickedEvent } from 'ag-grid-community'
import type { FEApiError } from 'utils/errorHandling'
import type { ProductsDetailDataV5 } from 'utils/utils.d'
import useInitialiser from 'hooks/useInitialiser'

interface RenderGeneratedProductProps {
  productId: ProductsDetailDataV5['product_id']
  projectRegionId: ProductsDetailDataV5['project_region_id']
  setToastDetails: Dispatch<SetStateAction<ToastDetails | undefined>>
  approveData: {
    project_region_id: string | undefined
    product_ids: string[] | undefined
  }
}

const { JOBS_API } = DEFAULT_API_ITEM_LIMITS

export default function RenderGeneratedProduct({
  productId,
  projectRegionId,
  setToastDetails,
  approveData,
}: RenderGeneratedProductProps) {
  const navigate = useNavigate()
  const [shouldJobsDataAppend, setShouldJobsDataAppend] = useState(false)
  const [isExportJobsLoading, setIsExportJobsLoading] = useState(false)
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false)
  const { userName } = useCurrentUser()
  const [tableReducerState, tableReducerDispatch] = useReducer(
    paginatedTableReducer,
    getPaginatedTableReducerEmptyState(),
  )

  const getJobsApiMethods = useApi<JobsListData>(
    `jobs?limit=${JOBS_API}&position=0&group_ids=${productId}&project_region_ids=${projectRegionId}`,
  )

  const {
    fetchData: getJobs,
    data: getJobsData,
    error: getJobsError,
    isLoading: isGetJobsLoading,
    url: getJobsUrl,
    setUrl: setJobsUrl,
  } = getJobsApiMethods

  const { fetchData: exportJobs } = useApi<string>(
    `jobs/export?group_ids=${productId}&project_region_ids=${projectRegionId}`,
    {},
    0,
    'csv',
  )
  const {
    fetchData: fetchApprovePost,
    isLoading: isFetchApproveLoading,
    url: fetchApproveUrl,
  } = useApi<ApproveCreateData>('jobs/approve')

  const { isInitialising } = useInitialiser({
    subscribedApis: [getJobsApiMethods],
  })

  // process jobs/tasks API response
  const processJobsData = () => {
    if (!getJobsData) return
    const { jobs, position, item_count, total_item_count } = getJobsData

    tableReducerDispatch(
      setRowData({
        rowData: jobs || [],
        shouldAppend: shouldJobsDataAppend,
      }),
    )
    tableReducerDispatch(setTaskPosition(shouldJobsDataAppend ? position! + item_count! : 0))
    tableReducerDispatch(setTotalItemCount(total_item_count!))
  }

  // add data to table reducer every time isGetJobsLoading is true
  useEffect(() => {
    if (isGetJobsLoading || !getJobsData || getJobsError) return
    // set empty data messages every api call

    processJobsData()
    setShouldJobsDataAppend(false)
  }, [isGetJobsLoading])

  // get jobs data on mount
  useEffect(() => {
    getJobs(getJobsUrl).catch(console.error)
  }, [getJobsUrl])

  useEffect(() => {
    if (getJobsError) {
      setToastDetails({
        type: 'error',
        message: getJobsError.message || getJobsError?.errors?.[0].message || 'Failed to fetch jobs data',
      })
    }
  }, [getJobsError])

  const handleDownloadAll = () => {
    setIsExportJobsLoading(true)
    exportJobs()
      .then(exportedJobData => {
        downloadCSVFile(exportedJobData, 'product_task_output.csv')
      })
      .catch(console.error)
      .finally(() => {
        setIsExportJobsLoading(false)
      })
  }

  const handleRowClicked = (e: RowClickedEvent) => {
    const { _id } = e.data
    navigate(`/task/${_id}`)
  }

  const handleApproveAll = async () => {
    try {
      if (!approveData.project_region_id || !approveData.product_ids || !approveData.product_ids.length)
        throw new Error('Something went wrong, please try again')
      await fetchApprovePost(fetchApproveUrl, {
        method: 'POST',
        body: JSON.stringify({
          ...approveData,
          update_info: {
            user_name: userName,
          },
        }),
      })
      refreshHandler(setShouldJobsDataAppend, tableReducerDispatch, getJobsUrl, setJobsUrl)

      setToastDetails({
        message: 'Tasks approved successfully',
        type: 'success',
      })
      setIsApproveModalOpen(false)
    } catch (err) {
      const error = err as FEApiError
      const errMessage =
        error.errors?.[0].message ||
        error.message ||
        (error.errors?.[0] as string | undefined) ||
        'Something went wrong'
      throw new Error(errMessage)
    }
  }

  return (
    <>
      <TableMetaDataRow
        text={`Showing ${tableReducerState.totalItemCount} results`}
        refreshHandler={() => {
          refreshHandler(setShouldJobsDataAppend, tableReducerDispatch, getJobsUrl, setJobsUrl)
        }}
        isInitialising={isInitialising}
      >
        <>
          <DownloadAllButton onClick={handleDownloadAll} isLoaded={isExportJobsLoading} disabled={isInitialising} />

          <WppActionButton
            data-testid="approve-all-button"
            onClick={() => setIsApproveModalOpen(true)}
            disabled={isInitialising}
          >
            <WppIconDone slot="icon-start" />
            Approve all
          </WppActionButton>
        </>
      </TableMetaDataRow>
      <Table
        theme="tertiary"
        loading={isGetJobsLoading}
        loadedRows={tableReducerState.itemCount}
        totalRows={tableReducerState.totalItemCount}
        rowData={tableReducerState.rowData}
        columnDefs={columnStructure}
        onLoadMore={() => {
          handleLoadMore(getJobsData, setShouldJobsDataAppend, JOBS_API, tableReducerDispatch, getJobsUrl, setJobsUrl)
        }}
        onSortingUpdate={sortData => {
          handleSortingUpdate(sortData, setShouldJobsDataAppend, getJobsUrl, tableReducerDispatch, setJobsUrl)
        }}
        onRowClicked={handleRowClicked}
        isDrillable
        context={{ dispatcher: tableReducerDispatch }}
        isInitialising={isInitialising}
      />

      {isApproveModalOpen && (
        <GenericModal
          shouldModalOpen={isApproveModalOpen}
          modalTitle="Approve tasks"
          submitButtonText="Approve"
          onCancel={() => setIsApproveModalOpen(false)}
          isApiLoading={isFetchApproveLoading}
          onSubmit={handleApproveAll}
        >
          <WppTypography tag="p" type="s-body" data-testid="modal-disclaimer">
            Are you sure you want to approve <b>all tasks?</b>
            <br />
            This will move these into “completed” status.
          </WppTypography>
        </GenericModal>
      )}
    </>
  )
}
